import React, { useState } from 'react';
import { Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Guard } from '../types';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { GuardExpandedInfo } from './GuardExpandedInfo';
import { useUserRoles } from '@/hooks/useUserRoles';
import { ResponsiveGuardDetails } from './ResponsiveGuardDetails';

interface SearchDialogProps {
  guards: Guard[];
  onSelectGuard: (guard: Guard) => void;
  isLoading?: boolean;
  onGuardRemoved?: () => void;
}

export function SearchDialog({ guards = [], onSelectGuard, isLoading, onGuardRemoved }: SearchDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedGuard, setSelectedGuard] = useState<Guard | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { isAdmin } = useUserRoles();

  const handleGuardSelect = (guard: Guard) => {
    setSelectedGuard(guard);
    if (isAdmin) {
      setSheetOpen(true);
    }
    onSelectGuard(guard);
    setOpen(false);
  };

  const handleViewMore = (guard: Guard, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedGuard(guard);
    setSheetOpen(true);
  };

  if (isLoading) {
    return (
      <Button variant="outline" className="w-full" disabled>
        <Search className="mr-2 h-4 w-4 flex-shrink-0" />
        <span className="truncate">Loading...</span>
      </Button>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full">
            <Search className="mr-2 h-4 w-4 flex-shrink-0" />
            <span className="truncate">Search Users</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[625px]">
          <DialogHeader>
            <DialogTitle>Search Users</DialogTitle>
            <DialogDescription>
              Search for users by name, code, or mobile number.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col">
            <Command className="rounded-lg border shadow-md">
              <CommandInput placeholder="Type to search..." />
              <CommandList>
                <CommandEmpty>No user found.</CommandEmpty>
                <CommandGroup>
                  {guards.map((guard) => (
                    <CommandItem
                      key={guard.id}
                      value={`${guard.full_name || `${guard.first_name} ${guard.last_name}`} ${guard.code} ${guard.mobile_phone}`}
                      onSelect={() => handleGuardSelect(guard)}
                      className="flex items-center justify-between py-3"
                    >
                      <div className="flex flex-col">
                        <div className="font-medium">{guard.full_name || `${guard.first_name} ${guard.last_name}`}</div>
                        <div className="text-sm text-muted-foreground">
                          Code: {guard.code}
                          {guard.mobile_phone && ` • Phone: ${guard.mobile_phone}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={guard.status === 'active' ? 'default' : 'secondary'}>
                          {guard.status}
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8"
                          onClick={(e) => handleViewMore(guard, e)}
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          View More
                        </Button>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </DialogContent>
      </Dialog>

      {selectedGuard && (
        <ResponsiveGuardDetails
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          guard={selectedGuard}
          onGuardRemoved={() => {
            setSheetOpen(false);
            if (onGuardRemoved) onGuardRemoved();
          }}
          title="Πλήρη Στοιχεία Φύλακα"
          description={`${selectedGuard.first_name} ${selectedGuard.last_name}`}
        />
      )}
    </>
  );
}
